import { useEffect, useMemo, useState } from 'react';
import {
  FontIcon,
  MessageBar,
  MessageBarType,
  Stack,
  Text,
} from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import {
  PagedDataGrid,
  type DataGridColumn,
  type ICommandBarItem,
} from '@moko/ui';
import { getApiErrorMessage } from '@moko/utils';
import {
  getAutomaticSurcharges,
  type IAutomaticSurchargeRule,
} from '../../services/automaticSurchargeService';
import {
  getPricingItems,
  type IPricingItem,
} from '../../services/pricingService';

export type SurchargeRow = {
  id: number;
  code: string;
  name: string;
  enabled: boolean;
  countryCode: string | null;
  stateCode: string | null;
};

const locationLabel = (countryCode: string | null, stateCode: string | null) =>
  countryCode
    ? [countryCode, stateCode].filter(Boolean).join(' / ')
    : 'All locations';

const buildRows = (
  items: IPricingItem[],
  rules: IAutomaticSurchargeRule[]
): SurchargeRow[] => {
  const saved = new Map(rules.map((rule) => [rule.pricingItemId, rule]));
  const rows: SurchargeRow[] = items.flatMap((item) => {
    if (typeof item.id !== 'number') return [];
    const rule = saved.get(item.id);
    return [
      {
        id: item.id,
        code: item.code,
        name: item.name,
        enabled: rule?.enabled ?? false,
        countryCode: rule?.countryCode ?? null,
        stateCode: rule?.stateCode ?? null,
      },
    ];
  });
  rules.forEach((rule) => {
    if (items.some((item) => item.id === rule.pricingItemId)) return;
    rows.push({
      id: rule.pricingItemId,
      code: rule.code,
      name: `${rule.name} — unavailable`,
      enabled: rule.enabled,
      countryCode: rule.countryCode,
      stateCode: rule.stateCode,
    });
  });
  return rows.sort((a, b) => a.name.localeCompare(b.name));
};

export const loadSurchargeRows = async (
  controller: AbortController
): Promise<SurchargeRow[]> => {
  const catalog = async () => {
    const first = await getPricingItems(controller, 500, 1);
    const catalogRows = [...first.items];
    for (let page = 2; page <= first.pageCount; page++)
      catalogRows.push(...(await getPricingItems(controller, 500, page)).items);
    return catalogRows;
  };
  const [stored, all] = await Promise.all([
    getAutomaticSurcharges(controller),
    catalog(),
  ]);
  const eligible = all.filter(
    (item) =>
      !item.isDeleted &&
      item.calculationMethod === 'Percentage' &&
      item.chargeRole === 'AdditionalCharge' &&
      item.quantityType === 'Fixed'
  );
  return buildRows(eligible, stored);
};

const columns: DataGridColumn<SurchargeRow>[] = [
  { key: 'name', name: 'Name', minWidth: 180, width: 280 },
  { key: 'code', name: 'Code', minWidth: 160, width: 240 },
  {
    key: 'enabled',
    name: 'Enabled',
    minWidth: 80,
    width: 110,
    value: (item) => (item.enabled ? 'Yes' : 'No'),
  },
  {
    key: 'location',
    name: 'Location',
    minWidth: 140,
    width: 180,
    value: (item) => locationLabel(item.countryCode, item.stateCode),
  },
];

export default function AutomaticSurcharges() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SurchargeRow[]>([]);
  const [selected, setSelected] = useState<SurchargeRow>();
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string>();
  const [reload, setReload] = useState(0);

  const edit = (row: SurchargeRow) => {
    void navigate(`/editAutomaticSurcharge?id=${row.id}`);
  };

  useEffect(() => {
    const controller = new AbortController();
    setLoaded(false);
    setError(undefined);
    loadSurchargeRows(controller)
      .then((next) => {
        if (controller.signal.aborted) return;
        setRows(next);
        setLoaded(true);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setError(getApiErrorMessage(e, 'Unable to load surcharge defaults.'));
        setLoaded(true);
      });
    return () => controller.abort();
  }, [reload]);

  const commands: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: 'Refresh',
        icon: <FontIcon iconName="Refresh" />,
        onClick: () => {
          setSelected(undefined);
          setReload((n) => n + 1);
        },
      },
      {
        key: 'editAutomaticSurcharge',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        disabled: !selected,
        onClick: () => {
          if (selected) edit(selected);
        },
      },
    ],
    [navigate, selected]
  );

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      <Text variant="large">Automatic surcharges</Text>
      <Text>
        Set the default for all tenants. Each tenant can inherit this default or
        override it in Pricing → Automatic surcharges. Manage percentages and
        effective dates in Pricing rates.
      </Text>
      {error && (
        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
      )}
      <PagedDataGrid<SurchargeRow>
        columns={columns}
        items={rows}
        loading={!loaded}
        commandBarItems={commands}
        selectionMode="single"
        selectedId={selected?.id ?? null}
        onSelectionChange={setSelected}
        onRowDoubleClicked={edit}
        empty={{
          title:
            'Create a fixed Percentage AdditionalCharge pricing item first.',
        }}
      />
    </Stack>
  );
}
